import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Save, X, Zap, Droplets, Flame, ChevronDown, ChevronRight,
  Upload, Image as ImageIcon, FileText, Package, Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Default product type categories
const DEFAULT_CATEGORIES = [
  {
    id: 'electrical',
    name: 'Electrical',
    icon: '⚡',
    requiresElectricalSpecs: true,
    types: [
      'Light Fixture - Recessed', 'Light Fixture - Pendant', 'Light Fixture - Chandelier',
      'Light Fixture - Sconce', 'Light Fixture - Vanity', 'Light Fixture - Ceiling Fan',
      'Outlet - Standard', 'Outlet - GFCI', 'Switch - Dimmer', 'EV Charger'
    ]
  },
  {
    id: 'appliances',
    name: 'Appliances',
    icon: '🔌',
    requiresElectricalSpecs: true,
    types: [
      'Range - Electric', 'Range - Gas', 'Range Hood', 'Cooktop - Electric', 'Cooktop - Gas',
      'Wall Oven', 'Microwave', 'Dishwasher', 'Refrigerator', 'Garbage Disposal',
      'Washer', 'Dryer - Electric', 'Dryer - Gas'
    ]
  },
  {
    id: 'plumbing',
    name: 'Plumbing',
    icon: '💧',
    requiresPlumbingSpecs: true,
    types: [
      'Faucet - Kitchen', 'Faucet - Bathroom', 'Faucet - Tub', 'Faucet - Shower',
      'Sink - Kitchen', 'Sink - Bathroom', 'Toilet', 'Bathtub', 'Shower System',
      'Water Heater - Tank', 'Water Heater - Tankless'
    ]
  },
  {
    id: 'hvac',
    name: 'HVAC',
    icon: '❄️',
    requiresElectricalSpecs: true,
    requiresGasSpecs: true,
    types: [
      'Air Conditioner', 'Furnace - Gas', 'Furnace - Electric', 'Heat Pump',
      'Mini Split', 'Thermostat', 'Exhaust Fan - Bath', 'Exhaust Fan - Range'
    ]
  },
  {
    id: 'finishes',
    name: 'Finishes',
    icon: '🎨',
    types: [
      'Tile - Floor', 'Tile - Wall', 'Tile - Backsplash', 'Flooring - Hardwood',
      'Flooring - LVP', 'Countertop', 'Paint', 'Wallpaper'
    ]
  },
  {
    id: 'cabinetry',
    name: 'Cabinetry',
    icon: '🪵',
    types: [
      'Cabinet - Base', 'Cabinet - Wall', 'Cabinet - Vanity', 'Cabinet Hardware'
    ]
  },
  {
    id: 'doors_windows',
    name: 'Doors & Windows',
    icon: '🚪',
    types: [
      'Door - Interior', 'Door - Exterior', 'Door Hardware', 'Window', 'Skylight'
    ]
  },
  {
    id: 'hardware',
    name: 'Hardware',
    icon: '🔩',
    types: [
      'Towel Bar', 'Robe Hook', 'Toilet Paper Holder', 'Mirror', 'Medicine Cabinet'
    ]
  }
];

import RoomPicker from '@/components/rooms/RoomPicker';

export default function ManualClipEntry({ 
  isOpen, 
  onClose, 
  projectId, 
  projects = [], // List of projects for assignment (when accessed from global Clip Library)
  onClipCreated 
}) {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(['electrical']);
  const [availableProjects, setAvailableProjects] = useState(projects);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || '');
  
  // Fetch projects if not provided
  useEffect(() => {
    if (!projectId && projects.length === 0) {
      const fetchProjects = async () => {
        try {
          const response = await axios.get(`${API}/projects`);
          setAvailableProjects(response.data || []);
        } catch (error) {
          console.error('Error fetching projects:', error);
        }
      };
      fetchProjects();
    }
  }, [projectId, projects]);
  
  // Form data
  const [clipData, setClipData] = useState({
    title: '',
    product_type: '',
    product_category: '',
    manufacturer: '',
    model_number: '',
    finish: '',
    color: '',
    material: '',
    dimensions: '',
    price: '',
    unit_price: '',
    unit_of_measure: '',
    notes: '',
    
    // Room assignment
    room: '',
    room_id: '',
    room_location: '',
    preference_level: 'First Choice',
    
    // Electrical specs
    electrical_specs: {
      watts: '',
      volts: '120',
      amps: '',
      lumens: '',
      color_temp: '',
      cri: '',
      dimmable: false,
      circuit_type: '15A',
    },
    
    // Plumbing specs
    plumbing_specs: {
      gpm: '',
      rough_in_size: '',
      connection_type: '',
      water_sense: false,
    },
    
    // Gas specs
    gas_specs: {
      btu_input: '',
      btu_output: '',
      gas_type: 'natural',
      connection_size: '',
    },
    
    // Image
    images: [],
  });
  
  // Fetch admin-configured product types
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/product-types/admin`);
        if (response.data.categories) {
          setCategories(response.data.categories);
        }
      } catch (error) {
        console.log('Using default categories');
      }
    };
    fetchCategories();
  }, []);
  
  // Determine which spec sections to show based on product type
  const getSpecRequirements = () => {
    if (!clipData.product_type) return { electrical: false, plumbing: false, gas: false };
    
    const cat = categories.find(c => c.types?.includes(clipData.product_type));
    return {
      electrical: cat?.requiresElectricalSpecs || cat?.requires_electrical_specs || false,
      plumbing: cat?.requiresPlumbingSpecs || cat?.requires_plumbing_specs || false,
      gas: cat?.requiresGasSpecs || cat?.requires_gas_specs || false,
    };
  };
  
  const specRequirements = getSpecRequirements();
  
  const handleProductTypeSelect = (type, category) => {
    setClipData(prev => ({
      ...prev,
      product_type: type,
      product_category: category.id,
    }));
    setSelectedCategory(category);
  };
  
  const toggleCategory = (catId) => {
    setExpandedCategories(prev => 
      prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
    );
  };
  
  const handleSubmit = async () => {
    if (!clipData.title || !clipData.product_type) {
      toast.error('Please enter a title and select a product type');
      return;
    }
    
    // Require project assignment
    const finalProjectId = selectedProjectId || projectId;
    if (!finalProjectId) {
      toast.error('Please select a project');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...clipData,
        source: 'manual_entry',
        project_id: finalProjectId,
        electrical_specs: specRequirements.electrical ? clipData.electrical_specs : null,
        plumbing_specs: specRequirements.plumbing ? clipData.plumbing_specs : null,
        gas_specs: specRequirements.gas ? clipData.gas_specs : null,
      };
      
      const response = await axios.post(`${API}/clips`, payload);
      toast.success(`Created spec: ${clipData.title}`);
      onClipCreated?.(response.data);
      onClose();
      
      // Reset form
      setClipData({
        title: '',
        product_type: '',
        product_category: '',
        manufacturer: '',
        model_number: '',
        finish: '',
        color: '',
        material: '',
        dimensions: '',
        price: '',
        unit_price: '',
        unit_of_measure: '',
        notes: '',
        room: '',
        room_id: '',
        room_location: '',
        preference_level: 'First Choice',
        electrical_specs: { watts: '', volts: '120', amps: '', lumens: '', color_temp: '', cri: '', dimmable: false, circuit_type: '15A' },
        plumbing_specs: { gpm: '', rough_in_size: '', connection_type: '', water_sense: false },
        gas_specs: { btu_input: '', btu_output: '', gas_type: 'natural', connection_size: '' },
        images: [],
      });
    } catch (error) {
      console.error('Error creating clip:', error);
      toast.error('Failed to create spec sheet');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#BC5D41]" />
            Create Manual Spec Sheet
          </DialogTitle>
          <DialogDescription>
            Enter product specifications manually. Data entered here will flow to Pre-Engineering tools and Estimates.
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="specs">Specifications</TabsTrigger>
            <TabsTrigger value="location">Location</TabsTrigger>
            <TabsTrigger value="pricing">Pricing</TabsTrigger>
          </TabsList>
          
          {/* Basic Info Tab */}
          <TabsContent value="basic" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-6">
              {/* Spec Category Selection */}
              <div className="space-y-3">
                <Label className="font-medium">Spec Category *</Label>
                <Card className="max-h-[300px] overflow-y-auto">
                  <CardContent className="p-2">
                    {categories.map(category => {
                      const isExpanded = expandedCategories.includes(category.id);
                      return (
                        <div key={category.id} className="mb-1">
                          <button
                            onClick={() => toggleCategory(category.id)}
                            className="w-full flex items-center gap-2 p-2 rounded hover:bg-gray-100 text-left"
                          >
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            <span>{category.icon}</span>
                            <span className="text-sm font-medium">{category.name}</span>
                            {category.requiresElectricalSpecs && <Zap className="w-3 h-3 text-yellow-500 ml-auto" />}
                            {category.requiresPlumbingSpecs && <Droplets className="w-3 h-3 text-blue-500" />}
                            {category.requiresGasSpecs && <Flame className="w-3 h-3 text-orange-500" />}
                          </button>
                          
                          {isExpanded && (
                            <div className="ml-6 space-y-0.5 pb-2">
                              {category.types?.map(type => (
                                <button
                                  key={type}
                                  onClick={() => handleProductTypeSelect(type, category)}
                                  className={`w-full text-left text-sm p-1.5 rounded transition-colors ${
                                    clipData.product_type === type
                                      ? 'bg-[#BC5D41]/20 text-[#70170F] font-medium'
                                      : 'hover:bg-gray-50'
                                  }`}
                                >
                                  {type}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
                
                {clipData.product_type && (
                  <div className="flex items-center gap-2 p-2 bg-[#BC5D41]/10 rounded-lg">
                    <Package className="w-4 h-4 text-[#BC5D41]" />
                    <span className="text-sm font-medium text-[#70170F]">{clipData.product_type}</span>
                  </div>
                )}
              </div>
              
              {/* Basic Details */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title / Name *</Label>
                  <Input
                    id="title"
                    value={clipData.title}
                    onChange={(e) => setClipData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Kohler Artifacts Faucet"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="manufacturer">Manufacturer</Label>
                    <Input
                      id="manufacturer"
                      value={clipData.manufacturer}
                      onChange={(e) => setClipData(prev => ({ ...prev, manufacturer: e.target.value }))}
                      placeholder="e.g., Kohler"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="model_number">Model Number</Label>
                    <Input
                      id="model_number"
                      value={clipData.model_number}
                      onChange={(e) => setClipData(prev => ({ ...prev, model_number: e.target.value }))}
                      placeholder="e.g., K-72759"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="finish">Finish</Label>
                    <Input
                      id="finish"
                      value={clipData.finish}
                      onChange={(e) => setClipData(prev => ({ ...prev, finish: e.target.value }))}
                      placeholder="e.g., Brushed Nickel"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="color">Color</Label>
                    <Input
                      id="color"
                      value={clipData.color}
                      onChange={(e) => setClipData(prev => ({ ...prev, color: e.target.value }))}
                      placeholder="e.g., Chrome"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="material">Material</Label>
                    <Input
                      id="material"
                      value={clipData.material}
                      onChange={(e) => setClipData(prev => ({ ...prev, material: e.target.value }))}
                      placeholder="e.g., Brass"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="dimensions">Dimensions</Label>
                  <Input
                    id="dimensions"
                    value={clipData.dimensions}
                    onChange={(e) => setClipData(prev => ({ ...prev, dimensions: e.target.value }))}
                    placeholder="e.g., 8&quot; H x 5&quot; W"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={clipData.notes}
                    onChange={(e) => setClipData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    placeholder="Additional notes..."
                  />
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* Specifications Tab */}
          <TabsContent value="specs" className="space-y-4 mt-4">
            {/* Electrical Specs */}
            {specRequirements.electrical && (
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Zap className="w-4 h-4 text-yellow-500" />
                    Electrical Specifications
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Watts</Label>
                      <Input
                        type="number"
                        value={clipData.electrical_specs.watts}
                        onChange={(e) => setClipData(prev => ({
                          ...prev,
                          electrical_specs: { ...prev.electrical_specs, watts: e.target.value }
                        }))}
                        placeholder="e.g., 60"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Volts</Label>
                      <Select
                        value={clipData.electrical_specs.volts}
                        onValueChange={(v) => setClipData(prev => ({
                          ...prev,
                          electrical_specs: { ...prev.electrical_specs, volts: v }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="120">120V</SelectItem>
                          <SelectItem value="240">240V</SelectItem>
                          <SelectItem value="277">277V</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Amps</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={clipData.electrical_specs.amps}
                        onChange={(e) => setClipData(prev => ({
                          ...prev,
                          electrical_specs: { ...prev.electrical_specs, amps: e.target.value }
                        }))}
                        placeholder="Auto-calc"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Circuit</Label>
                      <Select
                        value={clipData.electrical_specs.circuit_type}
                        onValueChange={(v) => setClipData(prev => ({
                          ...prev,
                          electrical_specs: { ...prev.electrical_specs, circuit_type: v }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="15A">15A</SelectItem>
                          <SelectItem value="20A">20A</SelectItem>
                          <SelectItem value="30A">30A</SelectItem>
                          <SelectItem value="40A">40A</SelectItem>
                          <SelectItem value="50A">50A</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Light-specific fields */}
                  {clipData.product_type?.toLowerCase().includes('light') && (
                    <div className="grid grid-cols-4 gap-3 pt-2 border-t">
                      <div className="space-y-1">
                        <Label className="text-xs">Lumens</Label>
                        <Input
                          type="number"
                          value={clipData.electrical_specs.lumens}
                          onChange={(e) => setClipData(prev => ({
                            ...prev,
                            electrical_specs: { ...prev.electrical_specs, lumens: e.target.value }
                          }))}
                          placeholder="e.g., 800"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Color Temp (K)</Label>
                        <Select
                          value={clipData.electrical_specs.color_temp}
                          onValueChange={(v) => setClipData(prev => ({
                            ...prev,
                            electrical_specs: { ...prev.electrical_specs, color_temp: v }
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2700">2700K (Warm)</SelectItem>
                            <SelectItem value="3000">3000K (Soft White)</SelectItem>
                            <SelectItem value="3500">3500K (Neutral)</SelectItem>
                            <SelectItem value="4000">4000K (Cool White)</SelectItem>
                            <SelectItem value="5000">5000K (Daylight)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">CRI</Label>
                        <Input
                          type="number"
                          value={clipData.electrical_specs.cri}
                          onChange={(e) => setClipData(prev => ({
                            ...prev,
                            electrical_specs: { ...prev.electrical_specs, cri: e.target.value }
                          }))}
                          placeholder="e.g., 90"
                        />
                      </div>
                      <div className="space-y-1 flex items-end pb-1">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={clipData.electrical_specs.dimmable}
                            onChange={(e) => setClipData(prev => ({
                              ...prev,
                              electrical_specs: { ...prev.electrical_specs, dimmable: e.target.checked }
                            }))}
                            className="w-4 h-4"
                          />
                          Dimmable
                        </label>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Plumbing Specs */}
            {specRequirements.plumbing && (
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Droplets className="w-4 h-4 text-blue-500" />
                    Plumbing Specifications
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Flow Rate (GPM)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={clipData.plumbing_specs.gpm}
                        onChange={(e) => setClipData(prev => ({
                          ...prev,
                          plumbing_specs: { ...prev.plumbing_specs, gpm: e.target.value }
                        }))}
                        placeholder="e.g., 1.5"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Rough-in Size</Label>
                      <Select
                        value={clipData.plumbing_specs.rough_in_size}
                        onValueChange={(v) => setClipData(prev => ({
                          ...prev,
                          plumbing_specs: { ...prev.plumbing_specs, rough_in_size: v }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1/2">1/2&quot;</SelectItem>
                          <SelectItem value="3/4">3/4&quot;</SelectItem>
                          <SelectItem value="10">10&quot; (toilet)</SelectItem>
                          <SelectItem value="12">12&quot; (toilet)</SelectItem>
                          <SelectItem value="14">14&quot; (toilet)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Connection</Label>
                      <Input
                        value={clipData.plumbing_specs.connection_type}
                        onChange={(e) => setClipData(prev => ({
                          ...prev,
                          plumbing_specs: { ...prev.plumbing_specs, connection_type: e.target.value }
                        }))}
                        placeholder="e.g., Compression"
                      />
                    </div>
                    <div className="space-y-1 flex items-end pb-1">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={clipData.plumbing_specs.water_sense}
                          onChange={(e) => setClipData(prev => ({
                            ...prev,
                            plumbing_specs: { ...prev.plumbing_specs, water_sense: e.target.checked }
                          }))}
                          className="w-4 h-4"
                        />
                        WaterSense®
                      </label>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Gas Specs */}
            {specRequirements.gas && (
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Flame className="w-4 h-4 text-orange-500" />
                    Gas Specifications
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">BTU Input</Label>
                      <Input
                        type="number"
                        value={clipData.gas_specs.btu_input}
                        onChange={(e) => setClipData(prev => ({
                          ...prev,
                          gas_specs: { ...prev.gas_specs, btu_input: e.target.value }
                        }))}
                        placeholder="e.g., 80000"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">BTU Output</Label>
                      <Input
                        type="number"
                        value={clipData.gas_specs.btu_output}
                        onChange={(e) => setClipData(prev => ({
                          ...prev,
                          gas_specs: { ...prev.gas_specs, btu_output: e.target.value }
                        }))}
                        placeholder="e.g., 76000"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Gas Type</Label>
                      <Select
                        value={clipData.gas_specs.gas_type}
                        onValueChange={(v) => setClipData(prev => ({
                          ...prev,
                          gas_specs: { ...prev.gas_specs, gas_type: v }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="natural">Natural Gas</SelectItem>
                          <SelectItem value="propane">Propane</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Connection Size</Label>
                      <Select
                        value={clipData.gas_specs.connection_size}
                        onValueChange={(v) => setClipData(prev => ({
                          ...prev,
                          gas_specs: { ...prev.gas_specs, connection_size: v }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1/2">1/2&quot;</SelectItem>
                          <SelectItem value="3/4">3/4&quot;</SelectItem>
                          <SelectItem value="1">1&quot;</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {!specRequirements.electrical && !specRequirements.plumbing && !specRequirements.gas && (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No technical specifications required for this product type.</p>
                <p className="text-sm">Select a product type with electrical, plumbing, or gas requirements to see specification fields.</p>
              </div>
            )}
          </TabsContent>
          
          {/* Location Tab */}
          <TabsContent value="location" className="space-y-4 mt-4">
            {/* Project Assignment */}
            {!projectId && (
              <Card className="border-[#BC5D41]/30 bg-[#BC5D41]/5">
                <CardHeader className="py-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Package className="w-4 h-4 text-[#BC5D41]" />
                    Project Assignment *
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Label>Assign to Project</Label>
                    <Select
                      value={selectedProjectId}
                      onValueChange={(v) => {
                        setSelectedProjectId(v);
                        // SP-3 — a room from one project cannot ride onto another.
                        setClipData((prev) => ({ ...prev, room: '', room_id: '' }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a project" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableProjects.map(project => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Show current project if assigned */}
            {projectId && (
              <div className="p-3 rounded-lg bg-green-50 border border-green-200 flex items-center gap-2">
                <Package className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-800">
                  This spec will be added to the current project
                </span>
              </div>
            )}
            
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base">Room Assignment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Room</Label>
                    <RoomPicker projectId={selectedProjectId || projectId || null}
                      value={{ id: clipData.room_id || null, name: clipData.room || '' }}
                      onChange={(r) => setClipData((prev) => ({ ...prev, room_id: r.id || '', room: r.name || '' }))}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      testid="manual-clip-room" />
                    {!(selectedProjectId || projectId) ? (
                      <p className="text-xs text-muted-foreground">Pick a project to choose from its rooms.</p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <Label>Specific Location</Label>
                    <Input
                      value={clipData.room_location}
                      onChange={(e) => setClipData(prev => ({ ...prev, room_location: e.target.value }))}
                      placeholder="e.g., Above vanity, Island pendant"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Preference Level</Label>
                    <Select
                      value={clipData.preference_level}
                      onValueChange={(v) => setClipData(prev => ({ ...prev, preference_level: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="First Choice">First Choice</SelectItem>
                        <SelectItem value="Option A">Option A</SelectItem>
                        <SelectItem value="Option B">Option B</SelectItem>
                        <SelectItem value="Option C">Option C</SelectItem>
                        <SelectItem value="Backup">Backup</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Pricing Tab */}
          <TabsContent value="pricing" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base">Pricing & Quantities</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>List Price</Label>
                    <Input
                      value={clipData.price}
                      onChange={(e) => setClipData(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="e.g., $299.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unit Price</Label>
                    <Input
                      value={clipData.unit_price}
                      onChange={(e) => setClipData(prev => ({ ...prev, unit_price: e.target.value }))}
                      placeholder="e.g., $24.99"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unit of Measure</Label>
                    <Input
                      value={clipData.unit_of_measure}
                      onChange={(e) => setClipData(prev => ({ ...prev, unit_of_measure: e.target.value }))}
                      placeholder="e.g., Each, SF, LF"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-[#BC5D41] hover:bg-[#70170F]">
            {loading ? 'Creating...' : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Create Spec Sheet
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
